/* Mock mailer: renders contracts/email-templates.json into the branded layout (same structure as the Go
 * html/template layout), keeps an outbox ("developer mailbox"), and mirrors the Go provider catalogue rules
 * (normalisation, validation, DNS guidance). Delivery is simulated synchronously. */
import CATALOG_JSON from "../../../../contracts/email-templates.json";
import PROVIDERS_JSON from "../../../../contracts/email-providers.json";
import type * as T from "../types";
import { randomHex, type Ctx } from "./ctx";
import type { MockEmail, MockEmailProvider, MockState } from "./state";

/* ---------- catalogue types ---------- */
interface LangContent {
  subject: string; preheader: string; heading: string; intro: string[]; list?: string[]; facts?: [string, string][];
  table?: { head: string[]; rows: string } | null; button?: { label: string; url: string } | null; outro?: string[];
}
export interface TemplateDef { category: string; essential: boolean; pref: string | null; sample: Record<string, unknown>; en: LangContent; ar: LangContent }
interface Catalog { version: number; why: Record<string, [string, string]>; templates: Record<string, TemplateDef> }
export interface ProviderRegion { id: string; label: string; labelAr?: string; host?: string; apiBase?: string; spf?: string }
export interface DnsTemplate { type: "TXT" | "CNAME" | "MX"; host: string; value: string }
export interface ProviderInfo {
  id: T.EmailProviderId; name: string; nameAr: string; kind: "smtp" | "api" | "log"; initials: string; tone: string;
  fields: string[]; optional?: string[]; labels: Record<string, [string, string]>;
  defaults?: { host?: string; port?: number; security?: T.SmtpSecurity; authMethod?: "plain" | "login"; region?: string; messageStream?: string };
  regions?: ProviderRegion[]; regionFree?: boolean; hostTemplate?: string; ports?: { port: number; security: T.SmtpSecurity }[];
  apiBase?: string; spf: string; spfHost?: string; dkim: [string, string]; dkimRecords: DnsTemplate[]; returnPathRecord?: DnsTemplate; notes: [string, string][];
}

export const CATALOG = CATALOG_JSON as unknown as Catalog;
export const PROVIDERS = (PROVIDERS_JSON as unknown as { providers: ProviderInfo[] }).providers;
export const providerInfo = (id: string) => PROVIDERS.find((p) => p.id === id);
export const TEMPLATE_KEYS = Object.keys(CATALOG.templates).sort();

/* ---------- rendering ---------- */
const PH = /\{\{\s*\.([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;
const fill = (s: string, data: Record<string, unknown>) => s.replace(PH, (_, k: string) => (data[k] == null ? "" : String(data[k])));
export const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&#34;").replace(/'/g, "&#39;");
const safeUrl = (u: string) => (/^https?:\/\//.test(u.trim()) ? u.trim() : "");

interface Section { lang: "en" | "ar"; heading: string; intro: string[]; list: string[]; facts: [string, string][]; head: string[]; rows: string[][]; btnLabel: string; btnUrl: string; outro: string[] }

function section(lang: "en" | "ar", lc: LangContent, data: Record<string, unknown>): Section {
  const f = (x: string) => fill(x, data);
  const rowsData = lc.table ? data[lc.table.rows] : undefined;
  const rows = Array.isArray(rowsData) ? (rowsData as unknown[][]).map((r) => r.map((c) => String(c))) : [];
  return {
    lang, heading: f(lc.heading), intro: lc.intro.map(f), list: (lc.list ?? []).map(f), outro: (lc.outro ?? []).map(f),
    facts: (lc.facts ?? []).map(([k, v]) => [f(k), f(v).trim()] as [string, string]).filter(([, v]) => v !== "" && v !== "·"),
    head: rows.length && lc.table ? lc.table.head.map(f) : [], rows,
    btnLabel: lc.button ? f(lc.button.label) : "", btnUrl: lc.button ? safeUrl(f(lc.button.url)) : "",
  };
}

const ADDRESS = "StartHR · Gulf Union Ozone Trading Co. · Jeddah, Saudi Arabia";

function layoutHtml(lang: "en" | "ar" | "bi", subject: string, preheader: string, sections: Section[], why: string[], unsub: string, year: number): string {
  const rtl = lang === "ar";
  const dir = rtl ? "rtl" : "ltr";
  const font = (d: string) => (d === "rtl" ? "'IBM Plex Sans Arabic',Tahoma,Arial,sans-serif" : "'IBM Plex Sans',Arial,Tahoma,sans-serif");
  const sec = sections.map((s, i) => {
    const d = s.lang === "ar" ? "rtl" : "ltr";
    const align = d === "rtl" ? "right" : "left";
    return `${i ? '<hr style="border:0;border-top:1px solid #e3e8e6;margin:28px 0;">' : ""}<div dir="${d}" lang="${s.lang}" style="text-align:${align};font-family:${font(d)};">`
      + `<h1 style="margin:0 0 16px;font-size:22px;line-height:1.35;color:#10231c;">${esc(s.heading)}</h1>`
      + s.intro.map((p) => `<p style="margin:0 0 14px;font-size:15px;line-height:1.65;color:#2b3a34;">${esc(p)}</p>`).join("")
      + (s.list.length ? `<ul style="margin:0 0 16px;padding-${align}:22px;">${s.list.map((x) => `<li style="margin:0 0 6px;">${esc(x)}</li>`).join("")}</ul>` : "")
      + (s.facts.length ? `<table role="presentation" class="fact" width="100%" style="margin:6px 0 18px;border:1px solid #e3e8e6;border-radius:8px;">${s.facts.map(([k, v]) => `<tr><td style="padding:10px 14px;color:#5d6e67;width:42%;">${esc(k)}</td><td style="padding:10px 14px;color:#10231c;font-weight:600;">${esc(v)}</td></tr>`).join("")}</table>` : "")
      + (s.head.length ? `<table role="presentation" class="fact" width="100%" style="margin:6px 0 18px;border:1px solid #e3e8e6;"><tr>${s.head.map((h) => `<td class="th" style="padding:8px 10px;background:#eef3f1;font-weight:700;">${esc(h)}</td>`).join("")}</tr>${s.rows.map((r) => `<tr>${r.map((c) => `<td style="padding:8px 10px;">${esc(c)}</td>`).join("")}</tr>`).join("")}</table>` : "")
      + (s.btnUrl ? `<table role="presentation" class="btn" align="${align}" style="margin:8px 0 20px;"><tr><td style="border-radius:8px;background:#0f5b46;"><a href="${esc(s.btnUrl)}" target="_blank" rel="noopener" style="display:inline-block;padding:13px 26px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;border-radius:8px;border-bottom:3px solid #c9a227;">${esc(s.btnLabel)}</a></td></tr></table><div style="clear:both;"></div>` : "")
      + s.outro.map((p) => `<p class="mut" style="margin:0 0 12px;font-size:13px;line-height:1.6;color:#5d6e67;">${esc(p)}</p>`).join("")
      + (s.btnUrl ? `<p class="mut" style="margin:14px 0 0;font-size:12px;color:#7b8b85;word-break:break-all;">${s.lang === "ar" ? "إذا لم يعمل الزر فانسخ هذا الرابط في المتصفح:" : "If the button doesn't work, copy this link into your browser:"} <a href="${esc(s.btnUrl)}">${esc(s.btnUrl)}</a></p>` : "")
      + "</div>";
  }).join("");
  const unsubLabel = rtl ? "إلغاء الاشتراك في هذه الإشعارات" : lang === "bi" ? "Unsubscribe from these notifications · إلغاء الاشتراك" : "Unsubscribe from these notifications";
  const tagline = rtl ? "الموارد البشرية والرواتب للمنشآت السعودية" : "HR & payroll for Saudi businesses";
  return `<!DOCTYPE html><html lang="${rtl ? "ar" : "en"}" dir="${dir}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="color-scheme" content="light dark"><meta name="supported-color-schemes" content="light dark"><title>${esc(subject)}</title>`
    + `<style>body{margin:0;background:#f2f4f3}@media (prefers-color-scheme: dark){body,.bg{background:#0c1311!important}.card{background:#141c19!important}.tx,.tx p,.tx td{color:#e4ebe8!important}}</style></head>`
    + `<body class="bg"><div data-text="skip" style="display:none;max-height:0;overflow:hidden;">${esc(preheader)}</div>`
    + `<table role="presentation" width="100%" class="bg" style="background:#f2f4f3;"><tr><td align="center" style="padding:24px 12px;"><table role="presentation" width="600" style="width:600px;max-width:600px;">`
    + `<tr><td dir="${dir}" style="background:#0f5b46;padding:20px 32px;border-radius:12px 12px 0 0;text-align:${rtl ? "right" : "left"};"><span style="font-size:22px;font-weight:700;color:#ffffff;">Start<span style="color:#d9b54a;">HR</span></span> <span style="font-size:12px;color:#cfe3db;">${esc(tagline)}</span></td></tr>`
    + `<tr><td style="height:4px;background:#c9a227;"></td></tr><tr><td class="card tx" style="background:#ffffff;padding:28px 32px 24px;">${sec}</td></tr>`
    + `<tr><td dir="${dir}" style="padding:20px 32px 8px;font-size:12px;color:#7b8b85;text-align:${rtl ? "right" : "left"};">${why.map((w) => `<p style="margin:0 0 6px;">${esc(w)}</p>`).join("")}`
    + (unsub ? `<p style="margin:0 0 6px;"><a href="${esc(unsub)}" style="color:#5d6e67;">${esc(unsubLabel)}</a></p>` : "")
    + `<p style="margin:8px 0 0;">${esc(ADDRESS)} · © ${year}</p></td></tr></table></td></tr></table></body></html>`;
}

function layoutText(sections: Section[], why: string[], unsub: string, unsubLabel: string, year: number): string {
  const out: string[] = [];
  sections.forEach((s, i) => {
    if (i) out.push("", "------------------------------------------------------------", "");
    out.push(s.heading, "");
    for (const p of s.intro) out.push(p, "");
    for (const x of s.list) out.push("• " + x);
    if (s.list.length) out.push("");
    for (const [k, v] of s.facts) out.push(`${k}: ${v}`);
    if (s.facts.length) out.push("");
    if (s.head.length) { out.push(s.head.join(" | ")); for (const r of s.rows) out.push(r.join(" | ")); out.push(""); }
    if (s.btnUrl) out.push(s.btnLabel + ":", s.btnUrl, "");
    for (const p of s.outro) out.push(p, "");
  });
  out.push("--", ...why);
  if (unsub) out.push(`${unsubLabel}: ${unsub}`);
  out.push(`${ADDRESS} · © ${year}`);
  return out.join("\n") + "\n";
}

export interface Rendered { subject: string; preheader: string; html: string; text: string; category: string; essential: boolean; pref: string | null; lang: "en" | "ar" | "bi" }

/** Render a catalogue template (same rules as api/internal/email.Renderer). */
export function renderTemplate(key: string, langIn: string, dataIn: Record<string, unknown>, unsub = "", year = 2026): Rendered {
  const t = CATALOG.templates[key];
  if (!t) throw new Error("unknown template " + key);
  const lang: "en" | "ar" | "bi" = langIn === "ar" ? "ar" : langIn === "bi" ? "bi" : "en";
  const data = dataIn;
  const whyPair = CATALOG.why[t.category] ?? ["", ""];
  const u = t.essential ? "" : unsub;
  let subject: string, preheader: string, sections: Section[], why: string[];
  if (lang === "bi") {
    subject = fill(t.en.subject, data) + " | " + fill(t.ar.subject, data);
    preheader = fill(t.en.preheader, data);
    sections = [section("en", t.en, data), section("ar", t.ar, data)];
    why = [fill(whyPair[0], data), fill(whyPair[1], data)];
  } else {
    const lc = lang === "ar" ? t.ar : t.en;
    subject = fill(lc.subject, data); preheader = fill(lc.preheader, data);
    sections = [section(lang, lc, data)];
    why = [fill(lang === "ar" ? whyPair[1] : whyPair[0], data)];
  }
  const unsubLabel = lang === "ar" ? "إلغاء الاشتراك في هذه الإشعارات" : "Unsubscribe from these notifications";
  return { subject, preheader, html: layoutHtml(lang, subject, preheader, sections, why, u, year), text: layoutText(sections, why, u, unsubLabel, year), category: t.category, essential: t.essential, pref: t.pref, lang };
}

/** Notification preference keys with their group (same order as Go Renderer.Preferences). */
export function preferenceKeys(): [string, T.NotificationPref["group"]][] {
  const seen = new Set<string>();
  const out: [string, T.NotificationPref["group"]][] = [];
  for (const k of TEMPLATE_KEYS) {
    const t = CATALOG.templates[k];
    if (!t.pref || seen.has(t.pref)) continue;
    seen.add(t.pref);
    out.push([t.pref, t.category as T.NotificationPref["group"]]);
  }
  const order: Record<string, number> = { billing: 0, hr: 1, compliance: 2 };
  return out.sort((a, b) => (order[a[1]] - order[b[1]]) || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
}

/* ---------- provider rules (mirror api/internal/email/catalog.go) ---------- */
export const validAddress = (s: string) => s.length <= 254 && /^[^\s@<>()",;:\\[\]]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/.test(s);
export const emailDomain = (a: string) => { const i = a.lastIndexOf("@"); return i >= 0 ? a.slice(i + 1).toLowerCase() : ""; };
const awsRegion = (s: string) => /^[a-z]{2}(-[a-z]+)+-\d+$/.test(s) && s.length <= 25;
const hostOk = (h: string) => h.length <= 253 && !/[ /:@?#]/.test(h) && h.split(".").every((l) => l.length > 0 && l.length <= 63);
const isLocal = (h: string) => h === "localhost" || h === "127.0.0.1" || h === "::1";

export function normalizeProvider(c: MockEmailProvider): MockEmailProvider {
  const p = providerInfo(c.provider);
  if (!p) return c;
  const d = p.defaults ?? {};
  const n = { ...c };
  if (!n.region && p.fields.includes("region")) n.region = d.region ?? "";
  if (!n.port && d.port) n.port = d.port;
  if (!n.security) n.security = d.security ?? "";
  if (!n.authMethod) n.authMethod = d.authMethod ?? "";
  if (!n.messageStream) n.messageStream = d.messageStream ?? "";
  if (p.kind === "smtp" && (!p.fields.includes("host") || !n.host?.trim())) {
    if (p.hostTemplate) n.host = p.hostTemplate.replace("{region}", n.region);
    else if (d.host) n.host = d.host;
    else { const r = p.regions?.find((x) => x.id === n.region); if (r?.host) n.host = r.host; }
  }
  if (p.kind === "smtp" && !n.security) n.security = "starttls";
  return n;
}

export function validateProvider(c: MockEmailProvider, prefix: string): Record<string, string> {
  const fe: Record<string, string> = {};
  const p = providerInfo(c.provider);
  if (!p) { fe[prefix + "provider"] = c.provider ? "unknown provider" : "required"; return fe; }
  const req = (f: string, v: string) => { if (p.fields.includes(f) && !(p.optional ?? []).includes(f) && !v.trim()) fe[prefix + f] = "required"; };
  req("host", c.host); req("username", c.username); req("secret", c.secret); req("domain", c.domain);
  if (p.fields.includes("region")) {
    if (!c.region) fe[prefix + "region"] = "required";
    else if (!p.regions?.some((r) => r.id === c.region) && !p.regionFree) fe[prefix + "region"] = "unknown region";
    else if (p.regionFree && !awsRegion(c.region)) fe[prefix + "region"] = "must look like me-central-1";
  }
  if (p.kind === "smtp") {
    if (!(c.port >= 1 && c.port <= 65535)) fe[prefix + "port"] = "must be between 1 and 65535";
    if (!["starttls", "tls", "none"].includes(c.security)) fe[prefix + "security"] = "must be starttls, tls or none";
    if (c.authMethod && c.authMethod !== "plain" && c.authMethod !== "login") fe[prefix + "authMethod"] = "must be plain or login";
    if (p.fields.includes("host") && c.host && !hostOk(c.host)) fe[prefix + "host"] = "must be a host name";
    if (c.security === "none" && c.username && !isLocal(c.host)) fe[prefix + "security"] = "a password cannot be sent without TLS — use STARTTLS or SSL/TLS";
  }
  if (c.endpoint && !/^https?:\/\//.test(c.endpoint)) fe[prefix + "endpoint"] = "must be an http(s) URL";
  return fe;
}

export function spfInclude(c: { provider: string; region: string }): string {
  const p = providerInfo(c.provider);
  if (!p) return "";
  return p.regions?.find((r) => r.id === c.region)?.spf || p.spf;
}

const fqdn = (h: string, d: string) => (!h || h === "@" ? d : h + "." + d);
function dmarc(d: string): T.DnsRecord {
  return { type: "TXT", host: "_dmarc." + d, value: `v=DMARC1; p=none; rua=mailto:dmarc-reports@${d}; adkim=r; aspf=r; pct=100`, purpose: "dmarc",
    note: "Start with p=none and read the reports; once SPF and DKIM pass, move to p=quarantine and then p=reject.",
    noteAr: "ابدأ بـ p=none وراجع التقارير؛ بعد نجاح SPF وDKIM انتقل إلى p=quarantine ثم p=reject." };
}

/** DNS records to publish (same output as Go email.DNSGuidance). */
export function dnsGuidance(from: string, c: { provider: string; region: string }): T.DnsGuidance {
  const domain = emailDomain(from);
  const g: T.DnsGuidance = { domain, provider: c.provider, records: [] };
  if (!domain) return g;
  const p = providerInfo(c.provider);
  if (!p || p.kind === "log") { g.records.push(dmarc(domain)); return g; }
  const inc = spfInclude(c);
  if (inc) g.records.push({ type: "TXT", host: fqdn(p.spfHost ?? "", domain), value: `v=spf1 ${inc} ~all`, purpose: "spf",
    note: `One SPF record per host: if one exists, add '${inc}' to it instead of creating a second record.`,
    noteAr: `سجل SPF واحد فقط لكل نطاق: إذا كان موجودًا فأضف '${inc}' إليه بدل إنشاء سجل ثانٍ.` });
  for (const r of p.dkimRecords) g.records.push({ type: r.type, host: fqdn(r.host, domain), value: r.value, purpose: "dkim", note: p.dkim[0] ?? "", noteAr: p.dkim[1] ?? "" });
  if (p.returnPathRecord) {
    let v = p.returnPathRecord.value;
    if (c.provider === "resend" && c.region) v = v.replace("us-east-1", c.region);
    g.records.push({ type: p.returnPathRecord.type, host: fqdn(p.returnPathRecord.host, domain), value: v, purpose: "return-path",
      note: "Custom return-path (bounce) domain — aligns SPF with your From domain for DMARC.", noteAr: "نطاق الارتداد المخصص — يجعل SPF متوافقًا مع نطاق المرسل لـ DMARC." });
  }
  g.records.push(dmarc(domain));
  return g;
}

/* ---------- outbox ---------- */
export interface EnqueueReq { tenantId: string; tenant: string; to: string; toName: string; lang: string; template: string; data: Record<string, unknown>; idem?: string }

let seq = 0;
const newId = () => "em-" + String(Date.now()).slice(-9) + "-" + String(++seq).padStart(6, "0") + randomHex(3);

export function unsubToken(email: string) { return "mock-" + b64(email.toLowerCase()); }
function b64(s: string) {
  let bin = "";
  for (const b of new TextEncoder().encode(s)) bin += String.fromCharCode(b);
  try { return btoa(bin).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_"); } catch { return s; }
}

export class Mailer {
  constructor(private c: Ctx) {}
  private get root(): MockState { return this.c.root; }
  private nowIso() { return new Date(this.c.wallNow()).toISOString().replace(/\.\d{3}Z$/, "Z"); }

  suppressed(email: string, essential: boolean): T.Suppression | null {
    const sp = this.root.suppressions[email.toLowerCase()];
    if (!sp) return null;
    if (sp.reason === "unsubscribe" && essential) return null;
    return sp;
  }

  /** Queue a message; duplicate idempotency key → the existing message. Delivery is simulated at once. */
  enqueue(r: EnqueueReq): MockEmail | null {
    const to = r.to.trim().toLowerCase();
    if (!validAddress(to)) return null;
    const t = CATALOG.templates[r.template];
    if (!t) return null;
    if (r.idem) { const ex = this.root.outbox.find((m) => m.idemKey === r.idem); if (ex) return ex; }
    const lang = r.lang === "en" || r.lang === "ar" ? r.lang : "bi";
    const base = this.c.baseUrl;
    const data: Record<string, unknown> = { Company: r.tenant, ...r.data };
    if (typeof data.Link === "string" && data.Link.startsWith("/")) data.Link = base + data.Link;
    const unsub = t.essential ? "" : `${base}/api/v1/public/email/unsubscribe?t=${unsubToken(to)}`;
    const out = renderTemplate(r.template, lang, data, unsub, new Date(this.c.wallNow()).getUTCFullYear());
    const now = this.nowIso();
    const id = newId();
    const m: MockEmail = { id, tenantId: r.tenantId, tenant: r.tenant, to, toName: r.toName, lang, template: r.template, category: out.category, essential: out.essential,
      subject: out.subject, status: "queued", attempts: 0, nextAttemptAt: now, providerId: "", provider: "", error: "", createdAt: now, sentAt: null,
      html: out.html, text: out.text, idemKey: r.idem ?? "auto:" + id, unsubscribe: unsub };
    const sp = this.suppressed(to, m.essential);
    if (sp) { m.status = "suppressed"; m.error = "suppressed: " + sp.reason; }
    this.root.outbox.push(m);
    if (m.status === "queued") this.deliver(m);
    return m;
  }

  /** Simulated send with the active configuration (same rules as the Go worker). */
  deliver(m: MockEmail) {
    const cfg = this.root.emailCfg;
    if (cfg.dailyLimit > 0) {
      const day = this.nowIso().slice(0, 10);
      const sentToday = this.root.outbox.filter((x) => x.status === "sent" && (x.sentAt ?? "").slice(0, 10) === day).length;
      if (sentToday >= cfg.dailyLimit) return; // stays queued
    }
    if (m.tenantId === this.c.seed.tenant.id && !cfg.sandboxTo && cfg.provider.provider !== "log") {
      m.status = "suppressed"; m.error = "demo company: emails are not sent to its sample addresses (set a sandbox inbox to receive them)"; return;
    }
    m.attempts += 1;
    m.provider = cfg.provider.provider;
    m.providerId = cfg.provider.provider + "-" + randomHex(8);
    m.status = "sent";
    m.sentAt = this.nowIso();
    if (cfg.sandboxTo) m.redirectedFrom = m.to;
  }

  /** Optional notification preference of a tenant. */
  prefEnabled(st: MockState | undefined, pref: string | null): boolean { return !pref || !st || !st.notifPrefs[pref]; }

  /** Queue a notification when the tenant has it enabled. */
  notify(tenantId: string, template: string, to: { email: string; name: string; lang: string; userId?: string }, data: Record<string, unknown>, idem?: string) {
    const t = CATALOG.templates[template];
    if (!t || !to.email) return null;
    const st = this.c.stateFor(tenantId);
    if (!this.prefEnabled(st, t.pref)) return null;
    const company = tenantId === "platform" ? "StartHR" : st ? (to.lang === "ar" && st.settings.tenant.nameAr ? st.settings.tenant.nameAr : st.settings.tenant.name) : "";
    return this.enqueue({ tenantId, tenant: st?.settings.tenant.name ?? "StartHR", to: to.email, toName: to.name, lang: to.lang, template, data: { Company: company, Name: to.name, ...data }, idem });
  }
}

export const firstName = (n: string) => { const s = n.trim(); const i = s.indexOf(" "); return i > 0 ? s.slice(0, i) : s; };

/** Recipient for a user (verified only). */
export interface Recipient { email: string; name: string; lang: string; userId: string }
export function userRecipient(_c: Ctx, u: T.User): Recipient {
  const lang: string = u.lang ?? "en";
  const name = lang === "ar" && u.nameAr ? u.nameAr : u.name;
  return { email: u.email, name: firstName(name), lang, userId: u.id };
}

/** Verified users of a tenant with one of `roles`. */
export function usersWithRoles(c: Ctx, tenantId: string, ...roles: T.Role[]): Recipient[] {
  return c.root.users.filter((u) => u.tenantId === tenantId && roles.includes(u.role) && !c.root.unverified.has(u.id)).map((u) => userRecipient(c, u));
}

/** Employee recipient: their (verified) user account, else the employee email (bilingual). */
export function employeeRecipient(c: Ctx, st: MockState, empId: number): Recipient | null {
  const e = st.employees.find((x) => x.id === empId);
  if (!e) return null;
  const u = c.root.users.find((x) => x.tenantId === st.tenantId && x.employeeId === empId && !c.root.unverified.has(x.id));
  if (u) { const r = userRecipient(c, u); if (r.lang === "ar" && e.nameAr) r.name = firstName(e.nameAr); return r; }
  if (!e.email || !validAddress(e.email.toLowerCase())) return null;
  return { email: e.email, name: firstName(e.nameEn), lang: "", userId: "" };
}

/** "AED 1,234.50" — moneySAR with a currency code (Go moneyIn). */
export function moneyIn(n: number, cur: string): string { return moneySAR(n).replace("SAR ", cur + " "); }
/** "SAR 1,234.50" (Go moneySAR). */
export function moneySAR(n: number): string {
  const neg = n < 0;
  const [i, f] = Math.abs(n).toFixed(2).split(".");
  return (neg ? "-" : "") + "SAR " + i.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + f;
}
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
export function periodLabels(period: string): [string, string] {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return [period, period];
  const mo = Number(m[2]) - 1;
  return [`${MONTHS_EN[mo]} ${m[1]}`, `${MONTHS_AR[mo]} ${m[1]}`];
}
export const LEAVE_NAMES: Record<string, [string, string]> = {
  Annual: ["Annual leave", "إجازة سنوية"], Sick: ["Sick leave", "إجازة مرضية"], Hajj: ["Hajj leave", "إجازة الحج"], Marriage: ["Marriage leave", "إجازة الزواج"],
  Bereavement: ["Bereavement leave", "إجازة الوفاة"], Maternity: ["Maternity leave", "إجازة الوضع"], Paternity: ["Paternity leave", "إجازة المولود"],
  "Sibling bereavement": ["Sibling bereavement leave", "إجازة وفاة الأخ أو الأخت"], Iddah: ["Iddah leave", "إجازة العدة"], "Nursing hour": ["Nursing hour", "ساعة الرضاعة"], Exam: ["Exam leave", "إجازة الامتحانات"],
};
export const LETTER_AR: Record<string, string> = {
  "Salary certificate": "شهادة الراتب", "Experience letter": "شهادة الخبرة", "No objection certificate": "شهادة عدم الممانعة", "Salary transfer letter": "خطاب تحويل الراتب", "Embassy letter": "خطاب السفارة",
};
